import { Select } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomModal, CustomTypography } from "sipli-fleet-core/components";
import "./DeviceManagementModals.css";

interface SelectOption {
  label: string;
  value: string | number;
}

interface AddFuelSensorModalProps {
  open: boolean;
  options: SelectOption[];
  loading: boolean;
  value?: string | number;
  notFoundContent: ReactNode;
  onSearch: (value: string) => void;
  onChange: (value: string | number | undefined) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

interface UninstallConfirmationModalProps {
  open: boolean;
  title: ReactNode;
  description: ReactNode;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const UninstallIllustration = () => (
  <svg
    className="device-management-confirm-illustration"
    viewBox="0 0 62 78"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M21.5 49.5 12.6 58.4a5.4 5.4 0 0 1-7.6 0 5.4 5.4 0 0 1 0-7.6l8.9-8.9a16.2 16.2 0 0 1 20-20l-9.6 9.6 6.4 6.4 9.6-9.6a16.2 16.2 0 0 1-20 20Z"
      fill="#8793BC"
    />
    <path
      d="M43.4 7.8h6.2l1.1 5.1 4.7 2.7 4.9-1.7 3.1 5.4-3.8 3.5v5.4l3.8 3.5-3.1 5.4-4.9-1.7-4.7 2.7-1.1 5.1h-6.2l-1.1-5.1-4.7-2.7-4.9 1.7-3.1-5.4 3.8-3.5v-5.4l-3.8-3.5 3.1-5.4 4.9 1.7 4.7-2.7 1.1-5.1Z"
      fill="#B2B8CF"
    />
    <circle cx="46.5" cy="25.5" r="5.4" fill="#8793BC" />
  </svg>
);

export const AddFuelSensorModal = ({
  open,
  options,
  loading,
  value,
  notFoundContent,
  onSearch,
  onChange,
  onCancel,
  onConfirm,
}: AddFuelSensorModalProps) => {
  const { t } = useTranslation();

  return (
    <CustomModal
      open={open}
      width={472}
      centered
      closable={false}
      footer={null}
      onCancel={onCancel}
      rootClassName="device-management-modal-root"
      className="device-management-modal"
    >
      <div className="device-management-add-fuel-modal">
        <CustomTypography.CustomTitle className="device-management-modal-title">
          {t("add_fuel_sensors")}
        </CustomTypography.CustomTitle>

        <div className="device-management-modal-field">
          <CustomTypography.CustomText className="device-management-modal-label">
            {t("fuel_sensor")}
          </CustomTypography.CustomText>
          <Select
            showSearch
            allowClear
            filterOption={false}
            placeholder={t("input")}
            options={options}
            value={value}
            loading={loading}
            className="device-management-modal-select"
            onSearch={onSearch}
            onChange={onChange}
            notFoundContent={notFoundContent}
          />
        </div>

        <div className="device-management-modal-actions">
          <CustomButton
            type="secondary"
            label={t("deny")}
            buttonSize="medium"
            className="device-management-modal-action-btn device-management-modal-deny-btn"
            onClick={onCancel}
          />
          <CustomButton
            type="primary"
            label={t("confirm_action")}
            buttonSize="medium"
            className="device-management-modal-action-btn !text-gray2"
            disabled={!value}
            onClick={onConfirm}
          />
        </div>
      </div>
    </CustomModal>
  );
};

export const UninstallConfirmationModal = ({
  open,
  title,
  description,
  loading = false,
  onCancel,
  onConfirm,
}: UninstallConfirmationModalProps) => {
  const { t } = useTranslation();

  return (
    <CustomModal
      open={open}
      width={472}
      centered
      closable={false}
      footer={null}
      onCancel={onCancel}
      rootClassName="device-management-modal-root"
      className="device-management-modal"
    >
      <div className="device-management-confirm-modal">
        <button
          type="button"
          className="device-management-confirm-close"
          aria-label={t("cancel")}
          onClick={onCancel}
        >
          <span aria-hidden="true">×</span>
        </button>

        <UninstallIllustration />

        <div className="device-management-confirm-copy">
          <CustomTypography.CustomTitle className="device-management-confirm-title">
            {title}
          </CustomTypography.CustomTitle>
          <CustomTypography.CustomText className="device-management-confirm-description">
            {description}
          </CustomTypography.CustomText>
        </div>

        <div className="device-management-modal-actions">
          <CustomButton
            type="secondary"
            label={t("deny")}
            buttonSize="medium"
            className="device-management-modal-action-btn device-management-modal-deny-btn"
            onClick={onCancel}
            disabled={loading}
          />
          <CustomButton
            type="primary"
            label={t("confirm_action")}
            buttonSize="medium"
            className="device-management-modal-action-btn !text-gray2"
            loading={loading}
            onClick={onConfirm}
          />
        </div>
      </div>
    </CustomModal>
  );
};
